import { Button } from '@/components/atoms';
import { Typography } from '@/components/atoms/Typography';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { useTranslation, useUIColors } from '@/core/hooks';
import React, { useRef } from 'react';
import { Modal, Platform, StyleSheet, View } from 'react-native';

export interface MessageOkDialogProps {
  visible: boolean;
  message: string;
  okText?: string;
  onOk: () => void;
}

/**
 * Compact centered message + OK — same shell as appointment success confirmation, no icon.
 */
export const MessageOkDialog: React.FC<Readonly<MessageOkDialogProps>> = ({
  visible,
  message,
  okText,
  onOk,
}) => {
  const colors = useUIColors();
  const { t } = useTranslation();
  // Callers clear `message` together with `visible`; keep the last text so the
  // fade-out does not show an empty card.
  const lastMessageRef = useRef(message);
  if (message) {
    lastMessageRef.current = message;
  }
  const displayMessage =
    Platform.OS === 'web' ? message || lastMessageRef.current : message;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onOk}>
      <View style={styles.overlay}>
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
          <Typography variant="headline" color="primary" style={styles.message}>
            {displayMessage}
          </Typography>
          <Button
            title={okText || t('common.ok')}
            variant="primary"
            onPress={onOk}
            fullWidth
            style={styles.button}
          />
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: UI_PRIMITIVES.overlayBackdrop,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  container: {
    borderRadius: 16,
    paddingVertical: 32,
    paddingHorizontal: 24,
    width: '100%',
    maxWidth: 340,
    alignItems: 'center',
    gap: 24,
  },
  message: {
    textAlign: 'center',
    width: '100%',
    lineHeight: 24,
  },
  button: {
    width: '100%',
    height: 48,
  },
});

export default MessageOkDialog;
